// Frontend resolver: returns the shift the employee is scheduled to work
// on a given day, honoring per-day overrides → week assignment → null.
//
// Implementation: thin wrapper around GET /shiftSchedules/current. The
// backend already does the override-vs-week resolution; we just translate
// the response into the same `{ shift, source }` shape and tolerate the
// "no schedule yet" case so callers can keep their existing fallback
// behavior unchanged.

import dayjsTZ, { dayjsInTz } from "../dayjs";
import { API } from "../../helpers/API/amplifyApi";

let inflight = new Map(); // simple dedupe across rapid re-opens of the same modal

const cacheKey = (employeeId, dateMs, tz) => {
  const dayKey = (tz ? dayjsInTz(tz, dateMs) : dayjsTZ(dateMs)).format(
    "YYYY-MM-DD"
  );
  return `${employeeId}#${dayKey}`;
};

/**
 * @param {string} employeeId
 * @param {number} [dateMs]
 * @param {string} [tz] optional IANA team timezone for day-key
 */
export async function fetchScheduledShift(
  employeeId,
  dateMs = Date.now(),
  tz
) {
  if (!employeeId) return { shift: null, reason: "no_employee" };

  const key = cacheKey(employeeId, dateMs, tz);
  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    try {
      const res = await API.get("shiftSchedules", "/shiftSchedules/current", {
        queryStringParameters: {
          employeeId,
          date: String(Number(dateMs)),
        },
      });
      return res || { shift: null, reason: "empty_response" };
    } catch (e) {
      // Network / IAM / unknown — degrade gracefully so the caller can
      // fall back to the existing "first applied shift" behavior.
      console.warn("[findScheduledShift] resolver failed:", e?.message || e);
      return { shift: null, reason: "resolver_error" };
    } finally {
      // Keep entries for ~30s; long enough to coalesce clock-in/clock-out
      // re-renders, short enough that override edits propagate quickly.
      setTimeout(() => inflight.delete(key), 30_000);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

function d(tz, value) {
  return tz ? dayjsInTz(tz, value) : dayjsTZ(value);
}

/**
 * Read wall-clock hour/minute from a shift template time.
 * Templates are absolute instants (epoch ms or ISO); we only need HH:mm
 * in the company/team zone so we can re-anchor onto "today".
 *
 * @param {number|string|Date|import('dayjs').Dayjs} value
 * @param {string} [tz]
 * @returns {{ hour: number, minute: number, second: number }|null}
 */
export function extractShiftWallClock(value, tz) {
  if (value == null || value === "") return null;
  const dj = d(tz, value);
  if (!dj.isValid()) return null;
  return {
    hour: dj.hour(),
    minute: dj.minute(),
    second: dj.second(),
  };
}

/**
 * Project a shift definition onto a calendar day (wall-clock times).
 * Overnight shifts (end ≤ start) roll the end into the next calendar day.
 *
 * @param {object} shift
 * @param {{ at?: import('dayjs').Dayjs|number|string|Date, timezone?: string }} [options]
 * @returns {{ label: string, start: import('dayjs').Dayjs, end: import('dayjs').Dayjs, minutes: number, name: string|null, shiftId: string|null }|null}
 */
export function shiftToPlanned(shift, options = {}) {
  if (!shift?.shiftStartTime || !shift?.shiftEndTime) return null;

  const tz = options.timezone;
  const at = d(tz, options.at ?? Date.now());
  const startHm = extractShiftWallClock(shift.shiftStartTime, tz);
  const endHm = extractShiftWallClock(shift.shiftEndTime, tz);
  if (!startHm || !endHm) return null;

  const buildOnDay = (day) => {
    let start = day
      .hour(startHm.hour)
      .minute(startHm.minute)
      .second(startHm.second || 0)
      .millisecond(0);
    let end = day
      .hour(endHm.hour)
      .minute(endHm.minute)
      .second(endHm.second || 0)
      .millisecond(0);
    // Overnight: end is next calendar day.
    if (!end.isAfter(start)) {
      end = end.add(1, "day");
    }
    return { start, end };
  };

  let { start, end } = buildOnDay(at.startOf("day"));

  // After midnight, still inside yesterday's overnight window → use that.
  if (options.preferActive !== false) {
    const y = buildOnDay(at.startOf("day").subtract(1, "day"));
    const nowMs = at.valueOf();
    if (
      nowMs >= y.start.valueOf() - 15 * 60 * 1000 &&
      nowMs <= y.end.valueOf()
    ) {
      start = y.start;
      end = y.end;
    }
  }

  const minutes = end.diff(start, "minute");
  return {
    label: `${start.format("HH:mm")} – ${end.format("HH:mm")}`,
    start,
    end,
    minutes: minutes > 0 ? minutes : 0,
    name: shift.shiftName || null,
    shiftId: shift.shiftId || null,
  };
}

/**
 * Pick the employee-relevant planned shift from one or more assigned
 * definitions (static employeeShiftId list). Preference:
 * 1) active window [start−15m, end] (latest start wins if several)
 * 2) soonest upcoming start
 * 3) most recently started (late for that shift)
 *
 * @param {Array<object|null|undefined>} shifts
 * @param {{ at?: import('dayjs').Dayjs|number|string|Date, timezone?: string }} [options]
 */
export function pickPlannedFromShifts(shifts, options = {}) {
  if (!Array.isArray(shifts) || shifts.length === 0) return null;

  const tz = options.timezone;
  const at = d(tz, options.at ?? Date.now());
  const earlyMs = 15 * 60 * 1000;
  const nowMs = at.valueOf();

  const planned = shifts
    .filter((s) => s && s.shiftStartTime && s.shiftEndTime)
    .map((s) => shiftToPlanned(s, { at, timezone: tz }))
    .filter(Boolean);

  if (planned.length === 0) return null;
  if (planned.length === 1) return planned[0];

  const active = planned.filter((p) => {
    const startMs = p.start.valueOf();
    const endMs = p.end.valueOf();
    return nowMs >= startMs - earlyMs && nowMs <= endMs;
  });
  if (active.length > 0) {
    return active.sort((a, b) => b.start.valueOf() - a.start.valueOf())[0];
  }

  const upcoming = planned
    .filter((p) => p.start.valueOf() - earlyMs > nowMs)
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());
  if (upcoming.length > 0) return upcoming[0];

  return planned.sort((a, b) => b.start.valueOf() - a.start.valueOf())[0];
}

/** Reasons that mean: employee is under a published rotation for the day. */
const ROTATION_COVERED_REASONS = new Set(["not_assigned"]);

/**
 * Enrich a rotation shift stub `{ shiftId }` with full times from a local
 * catalogue (the two company shifts: 10:00–18:30 / 13:30–21:30).
 */
function enrichShift(shift, catalogues = []) {
  if (!shift) return null;
  if (shift.shiftStartTime && shift.shiftEndTime) return shift;
  if (!shift.shiftId) return null;
  for (const list of catalogues) {
    if (!Array.isArray(list)) continue;
    const found = list.find((s) => s?.shiftId === shift.shiftId);
    if (found?.shiftStartTime && found?.shiftEndTime) {
      return { ...found, ...shift, shiftId: shift.shiftId };
    }
  }
  return shift;
}

/**
 * Resolve today's planned window for an employee.
 *
 * Priority:
 * 1. **Rotation** — published Orari assignment for the day (always wins)
 * 2. If rotation covers the dept/month but employee is **not assigned**
 *    today → no planned window (do NOT fall back to static links)
 * 3. Otherwise → static `employeeShiftId` links (the two fixed shifts)
 *
 * @param {object|null|undefined} scheduledResult from fetchScheduledShift
 *        (`undefined` = still loading)
 * @param {Array} appliedShifts employee-linked shift defs
 * @param {{ at?: *, timezone?: string, catalogueShifts?: Array }} [options]
 */
export function resolvePlannedShift(
  scheduledResult,
  appliedShifts,
  options = {}
) {
  const tz = options.timezone;
  const at = d(tz, options.at ?? Date.now());
  const catalogues = [
    appliedShifts,
    options.catalogueShifts,
  ];

  // Still loading rotation — wait (avoids flashing static hours for
  // rotated employees before /current returns).
  if (scheduledResult === undefined) {
    return null;
  }

  if (scheduledResult?.shift) {
    const enriched = enrichShift(scheduledResult.shift, catalogues);
    const planned = shiftToPlanned(enriched, { at, timezone: tz });
    if (planned) {
      return { ...planned, source: scheduledResult.source || "rotation" };
    }
  }

  // On a published schedule but not rostered today → no static fallback.
  if (ROTATION_COVERED_REASONS.has(scheduledResult?.reason)) {
    return null;
  }

  const applied = Array.isArray(appliedShifts)
    ? appliedShifts.filter(Boolean)
    : [];
  const picked = pickPlannedFromShifts(applied, { at, timezone: tz });
  return picked ? { ...picked, source: "static" } : null;
}
